import React, { useState, useEffect } from 'react';

interface EmissionTestData {
  numberPlate: string;
  vehicleClass: string;
  serialNumber: string;
  fuelType: string;
  noOfKms: string;
  issueDate: string;
  center: string;
  testFee: number;
  overallStatus: string;
  expiryDate: string;
}

interface FormErrors {
  [key: string]: string;
}

interface Config {
  vehicleClasses: string[];
  emissionOverallStatus: string[];
}

interface IsLoading {
  config: boolean;
}

interface StepThreeProps {
  emissionTestData: EmissionTestData;
  setEmissionTestData: React.Dispatch<React.SetStateAction<EmissionTestData>>;
  emissionTestImages: File[];
  setEmissionTestImages: React.Dispatch<React.SetStateAction<File[]>>;
  formErrors: FormErrors;
  config: Config;
  isLoading: IsLoading;
  handleFileChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    maxSizeMB: number,
    allowedTypes: string[]
  ) => void;
}

// ⬆️ near imports / top of file
const todayStr = new Date().toISOString().split('T')[0];
const clampToToday = (v: string) => (v && v > todayStr ? todayStr : v || '');

const StepThree: React.FC<StepThreeProps> = ({
  emissionTestData,
  setEmissionTestData,
  emissionTestImages,
  setEmissionTestImages,
  formErrors,
  config,
  isLoading,
  handleFileChange,
}) => {
  const [emissionImagePreviews, setEmissionImagePreviews] = useState<string[]>([]);
  const maxFileSizeMB = 5;
  const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

  // Generate image previews
  const generatePreviews = (files: File[], setPreviews: React.Dispatch<React.SetStateAction<string[]>>) => {
    const previews = files.map((file) => URL.createObjectURL(file));
    setPreviews((prev) => {
      prev.forEach(URL.revokeObjectURL);
      return previews;
    });
  };

  // Handle file removal
  const removeFile = (
    index: number,
    files: File[],
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    previews: string[],
    setPreviews: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    const updatedPreviews = previews.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    setPreviews(updatedPreviews);
    updatedPreviews.forEach(URL.revokeObjectURL);
  };

  // Update previews when files change
  useEffect(() => {
    generatePreviews(emissionTestImages, setEmissionImagePreviews);
    return () => emissionImagePreviews.forEach(URL.revokeObjectURL);
  }, [emissionTestImages]);

  return (
    <div className="container">
      <div className="mb-4">
        <h5>Emission Test Records</h5>
        <div className="row g-4">
          {/* Left Column */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label mb-2">Vehicle Class</label>
              <select
                className="form-select"
                style={{ width: '100%', padding: '10px' }}
                value={emissionTestData.vehicleClass || ''}
                onChange={(e) => setEmissionTestData({ ...emissionTestData, vehicleClass: e.target.value })}
                disabled={isLoading.config}
              >
                <option value="">Select Vehicle Class</option>
                {(config.vehicleClasses || []).map((vc: string) => (
                  <option key={vc} value={vc}>
                    {vc}
                  </option>
                ))}
              </select>
              {formErrors.vehicleClass && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.vehicleClass}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Serial Number</label>
              <input
                type="text"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={emissionTestData.serialNumber || ''}
                onChange={(e) => setEmissionTestData({ ...emissionTestData, serialNumber: e.target.value })}
                placeholder="Enter serial number"
              />
              {formErrors.serialNumber && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.serialNumber}
                </div>
              )}
            </div>
            <div className="mb-3">
                <label className="col-form-label mb-2">Issue Date</label>
                <input
                  type="date"
                  className="form-control"
                  style={{ width: '100%', padding: '10px 15px' }}
                  value={emissionTestData.issueDate || ''}
                  max={todayStr}                                  // ✅ blocks future days in the calendar UI
                  onChange={(e) =>
                    setEmissionTestData({
                      ...emissionTestData,
                      issueDate: clampToToday(e.target.value),    // ✅ clamps if browser lets a future date through
                    })
                  }
                />
                {formErrors.issueDate && (
                  <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                    {formErrors.issueDate}
                  </div>
                )}
              </div>

            <div className="mb-3">
              <label className="col-form-label mb-2">Test Center</label>
              <input
                type="text"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={emissionTestData.center || ''}
                onChange={(e) => setEmissionTestData({ ...emissionTestData, center: e.target.value })}
                placeholder="Enter test center"
              />
              {formErrors.center && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.center}
                </div>
              )}
            </div>
          </div>
          {/* Right Column */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label mb-2">Expiry Date</label>
              <input
                type="date"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={emissionTestData.expiryDate || ''}
                onChange={(e) => setEmissionTestData({ ...emissionTestData, expiryDate: e.target.value })}
              />
              {formErrors.expiryDate && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.expiryDate}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Test Fee</label>
              <input
                type="number"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={emissionTestData.testFee || ''}
                onChange={(e) => setEmissionTestData({ ...emissionTestData, testFee: parseFloat(e.target.value) || 0 })}
                placeholder="Enter test fee"
                min="0"
                step="0.01"
              />
              {formErrors.testFee && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.testFee}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Overall Status</label>
              <select
                className="form-select"
                style={{ width: '100%', padding: '10px' }}
                value={emissionTestData.overallStatus || ''}
                onChange={(e) => setEmissionTestData({ ...emissionTestData, overallStatus: e.target.value })}
                disabled={isLoading.config}
              >
                <option value="">Select Status</option>
                {(config.emissionOverallStatus || []).map((s: string) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              {formErrors.overallStatus && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.overallStatus}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Emission Test Images</label>
              <input
                type="file"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                multiple
                accept={allowedTypes.join(',')}
                onChange={(e) => handleFileChange(e, setEmissionTestImages, maxFileSizeMB, allowedTypes)}
              />
              <small className="text-muted">Max {maxFileSizeMB}MB per file, formats: JPEG, PNG, JPG</small>
              {formErrors.emissionTestImages && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.emissionTestImages}
                </div>
              )}
              {emissionTestImages.length > 0 && (
                <div className="mt-3">
                  <h6>Selected Files:</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {emissionTestImages.map((file, index) => (
                      <div key={index} className="position-relative">
                        <img
                          src={emissionImagePreviews[index]}
                          alt={file.name}
                          style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm position-absolute top-0 end-0"
                          style={{ padding: '2px 6px' }}
                          onClick={() =>
                            removeFile(index, emissionTestImages, setEmissionTestImages, emissionImagePreviews, setEmissionImagePreviews)
                          }
                        >
                          ×
                        </button>
                        <div style={{ fontSize: '12px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepThree;